import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { EnvironmentProviders, importProvidersFrom, inject, makeEnvironmentProviders, provideEnvironmentInitializer } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { provideAnimations } from '@angular/platform-browser/animations';

import { BaasI18n } from '@lvflanders/bar';
import { provideBaasInitialized } from '@lvflanders/bar-ng';

import { _BaasInternalLayoutService } from './baas-internal-layout.service';
import { BaasLayoutService } from './baas-layout/baas-layout.service';
import { _BarClassicWidgetsEngine } from './bar-classic-widgets-engine.provider';
import { BarDefaultLayoutState } from './bar-default-layout-state.provider';
import { BarLayoutFeature, BarLayoutFeatureKind } from './bar-layout.feature';
import { BAR_LAYOUT_I18N } from './bar-layout.i18n';
import { barLayoutInterceptor } from './bar-layout.interceptor';
import { BarLayoutInitializedHook } from './bar-layout-initialized.hook';
import { BarLayoutState } from './bar-layout-state.contract';
import { BAAS_LAYOUT_BURGERPROFIEL, BaasLayoutBurgerprofielOptions } from './burgerprofiel/baas-burgerprofiel-feature';
import { _BarBurgerprofielEngine } from './burgerprofiel/bar-burgerprofiel-engine-provider';
import { BAAS_LAYOUT_GVK, BAAS_LAYOUT_STATE } from './layout-tokens';

export function provideBarLayout(...features: BarLayoutFeature[]): EnvironmentProviders {
    return makeEnvironmentProviders([
        // NOTE dependency needed by layout (to avoid errors)
        importProvidersFrom([MatTooltipModule, MatButtonModule]),
        provideAnimations(),
        Location,
        {
            provide: LocationStrategy,
            useClass: PathLocationStrategy
        },
        _BaasInternalLayoutService,
        _BarBurgerprofielEngine,
        _BarClassicWidgetsEngine,
        provideEnvironmentInitializer(() => inject(BaasI18n).addTranslations('DOMAIN_BAR_LAYOUT', BAR_LAYOUT_I18N)),
        provideBaasInitialized(BarLayoutInitializedHook),
        BaasLayoutService,
        {
            provide: BAAS_LAYOUT_GVK,
            useValue: false
        },
        // default burgerprofiel options
        {
            provide: BAAS_LAYOUT_BURGERPROFIEL,
            useValue: { _forceBurgerprofiel: false } as BaasLayoutBurgerprofielOptions
        },
        // Providing services for the features
        features?.map((f) => f.providers)
    ]);
}

export function withLayoutInterception(options: { logging: boolean } = { logging: false }): BarLayoutFeature {
    return {
        kind: BarLayoutFeatureKind.Interception,
        providers: [provideHttpClient(withInterceptors([barLayoutInterceptor(options)]))]
    };
}

export function withLayoutState(settings: BarLayoutState): BarLayoutFeature {
    return {
        kind: BarLayoutFeatureKind.CustomSettings,
        providers: [
            {
                provide: BAAS_LAYOUT_STATE,
                useValue: settings
            }
        ]
    };
}

export function withDefaultLayoutState(): BarLayoutFeature {
    return {
        kind: BarLayoutFeatureKind.DefaultSettings,
        providers: [
            {
                provide: BAAS_LAYOUT_STATE,
                useClass: BarDefaultLayoutState
            }
        ]
    };
}

export function withLayoutGvk(enabled: boolean = true): BarLayoutFeature {
    return {
        kind: BarLayoutFeatureKind.Gvk,
        providers: [
            {
                provide: BAAS_LAYOUT_GVK,
                useValue: enabled
            }
        ]
    };
}
